// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Ícones — SVG de traço único, uma só cor
// ============================================================================
//
// PORQUE NÃO EMOJI
//   A versão anterior usava `✈`, `🚆`, `⛴`, `◆`, `↓`, `↑`. Três problemas, e os
//   três foram apontados pelo cliente:
//
//   1. **Não são de cor única.** O `🚆` e o `⛴` são pictogramas coloridos, e a
//      cor deles não é nossa — logo entra no mapa cor sem significado, quando o
//      sistema de cor é justamente o que dá sentido ao ecrã.
//   2. **Mudam de forma por sistema.** O mesmo emoji é desenhado de maneira
//      diferente no Windows, no Mac e no Android. Um ícone que muda de forma não
//      pode fazer parte de um sistema visual.
//   3. **Não escalam com a tipografia.** Ficam grandes e desalinhados em texto de
//      9 px, que é o tamanho da célula.
//
//   Estes usam `currentColor` e `1em`: herdam a cor e o tamanho do texto onde
//   estão. Um ícone dentro de uma barra escura fica claro sem ninguém o dizer.
//
// PORQUE INLINE E NÃO UMA BIBLIOTECA
//   São nove ícones. Uma dependência para nove caminhos SVG seria mais código a
//   instalar do que a escrever, e amarrava o desenho a decisões de outra pessoa.
//
// ⚠️ `aria-hidden` em todos: o significado está sempre também em texto — no
// tooltip, no cartão de resumo ou no painel. Um ícone anunciado por leitor de
// ecrã em cima do texto que o explica é ruído, não acessibilidade.
//
// UM SÓ `<path>` POR ÍCONE — e a razão é medida
//   A primeira versão usava 2 a 5 elementos por ícone. Os nós por dia subiram de
//   3,7 para 5,9 e o p95 de ~18 para ~29 ms — proporcional, como o *spike* previa.
//   Nas máquinas do escritório (factor ~2,5) isso passaria o limiar de 60 ms que o
//   ADR-001 fixou para reavaliar.
//
//   Um `<path>` aceita **vários sub-caminhos** no mesmo `d`, separados por `M`.
//   Cada ícone é agora `<svg>` + `<path>` = 2 nós, sem perder desenho nenhum.
// ============================================================================

interface Props {
  /** Tamanho em `em`, relativo ao texto onde o ícone está. */
  readonly em?: number;
  readonly className?: string;
}

function Svg({
  em = 1,
  className,
  children,
  traco = true,
}: Props & { children: React.ReactNode; traco?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      width={`${em}em`}
      height={`${em}em`}
      fill={traco ? 'none' : 'currentColor'}
      stroke={traco ? 'currentColor' : 'none'}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Voo. Requisito V8. */
export function IconeVoo(p: Props) {
  return (
    <Svg {...p} traco={false}>
      <path d="M8.6 1.6c.3-.5 1-.5 1.3 0 .2.3.2.7.1 1L9 6l4.6 2.2c.3.1.5.4.5.8v1.3c0 .4-.4.7-.8.6L8.9 9.6l-.6 3.1 1.5 1.1c.2.2.3.4.3.6v.3c0 .3-.3.5-.6.4l-2.3-.7-2.3.7c-.3.1-.6-.1-.6-.4v-.3c0-.2.1-.5.3-.6l1.5-1.1-.6-3.1-4.4 1.3c-.4.1-.8-.2-.8-.6V9c0-.3.2-.6.5-.8L7 6 6 2.6c-.1-.3-.1-.7.1-1 .3-.5 1-.5 1.3 0" />
    </Svg>
  );
}

/** Comboio (AVE). Requisito V8. */
export function IconeComboio(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3.5 3.5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2z M3.5 6h9 M5 10.5 3 14.5 M11 10.5l2 4 M4.5 13h7" />
    </Svg>
  );
}

/** Ferry ou barco. Requisito V8 — e o art. 9.º do Reg. 561/2006 tem regras próprias. */
export function IconeFerry(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2 9.5h12l-1.4 4.2a1 1 0 0 1-.95.68H4.35a1 1 0 0 1-.95-.68z M4 9.5V5.5h8v4 M8 5.5V2.5" />
    </Svg>
  );
}

/** Chegada. Substitui o `↓`. */
export function IconeChegada(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 2v8 M4.8 6.8 8 10l3.2-3.2 M2.5 13.5h11" />
    </Svg>
  );
}

/** Partida. Substitui o `↑`. */
export function IconePartida(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 14V6 M4.8 9.2 8 6l3.2 3.2 M2.5 2.5h11" />
    </Svg>
  );
}

/** Viatura decorada com a imagem de um cliente — restringe quem a pode usar. */
export function IconeDecoracao(p: Props) {
  return (
    <Svg {...p} traco={false}>
      <path d="M8 1.8 9.9 5.7l4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
    </Svg>
  );
}

/** Viatura indisponível: oficina, avaria, sinistro. */
export function IconeOficina(p: Props) {
  return (
    <Svg {...p}>
      <path d="M10.3 2.2a3.6 3.6 0 0 0-1.1 5.9l-6 6a1.3 1.3 0 0 0 1.8 1.8l6-6a3.6 3.6 0 0 0 4.3-4.8l-2 2-1.6-.3-.3-1.6z" />
    </Svg>
  );
}

/** Dia de rotação: chega de um itinerário e parte noutro. Objectivo O1. */
export function IconeRotacao(p: Props) {
  return (
    <Svg {...p}>
      <path d="M13 6.5A5 5 0 0 0 3.6 4.8 M3 2.5V5h2.5 M3 9.5a5 5 0 0 0 9.4 1.7 M13 13.5V11h-2.5" />
    </Svg>
  );
}

/** Tripulação dupla: dois condutores em simultâneo. Art. 8.º n.º 5. */
export function IconeDupla(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8.2 5A2.2 2.2 0 1 1 3.8 5a2.2 2.2 0 0 1 4.4 0 M2.2 13.2a3.8 3.8 0 0 1 7.6 0 M11 3.2a2.2 2.2 0 0 1 0 4.3 M12.2 9.6a3.8 3.8 0 0 1 1.6 3.6" />
    </Svg>
  );
}

/** Reserva: registo não confirmado. */
export function IconeReserva(p: Props) {
  return (
    <Svg {...p}>
      <path d="M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0 M8 5v3.2l2.2 1.4" />
    </Svg>
  );
}

export function IconeChevron({ aberto = false, ...p }: Props & { aberto?: boolean }) {
  return (
    <Svg {...p}>
      <path d={aberto ? 'M3.5 6.5 8 11l4.5-4.5' : 'M6 3.5 10.5 8 6 12.5'} />
    </Svg>
  );
}

/**
 * ⚠️⚠️ **Duas folhas sobrepostas — «este dia e de outro plano»** (D-1).
 *
 * 📗 Cliente, 2026-08-21: *«aparece a ferramenta como se fosse oficina e diz "noutro plano".
 * Qual e a logica disto?»* — e a chave inglesa era exactamente a logica errada.
 *
 * 📌 Icone proprio e nao reaproveitado: este projecto ja corrigiu duas vezes o mesmo sinal
 * a servir duas coisas (tres botoes com o `IconeLista`, dois com o `IconeInfo`). Um so `<path>`,
 * como todos — e orcamento de nos medido.
 */
export function IconeOutroPlano(p: Props) {
  return (
    <Svg {...p}>
      <path d="M5.5 3.5h7v7h-7zM3.5 5.5v7h7" />
    </Svg>
  );
}

/** Lista de pedidos. Três linhas com marca à esquerda — a fila de trabalho. */
export function IconeLista(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3 4.5h1.5M3 8h1.5M3 11.5h1.5M6.5 4.5H13M6.5 8H13M6.5 11.5H13" />
    </Svg>
  );
}

/**
 * ⚠️⚠️ **QUATRO ICONES NOVOS PORQUE TRES BOTOES USAVAM O MESMO.**
 *
 * 📗 Cliente, 2026-08-10: *«está um pouco feio e confuso assim, melhore o design; os botões
 * todos em cima uns dos outros… o título precisa de ser ajustado»*.
 *
 * ⚠️⚠️ **A queixa era de arrumacao e havia um defeito por baixo:** «Pedidos», «Consultas» e
 * «Gestão» levavam todos `IconeLista`, e «Legenda» e a sessão levavam ambos `IconeInfo`. PIN
 * Sete pastilhas iguais com tres icones repetidos nao sao um menu: sao uma parede onde o icone
 * nao ajuda a encontrar nada e ainda ocupa espaco a fingir que ajuda. E o mesmo erro que este
 * projecto ja corrigiu duas vezes com os nomes de classe — **duas coisas diferentes nunca com o
 * mesmo sinal**.
 *
 * ⚠️ Um `<path>` cada, como todos os outros: e orcamento de nos, medido (ver o cabecalho).
 */

/** Engrenagem — configuração. Oito dentes e um centro, num só `d`. */
export function IconeEngrenagem(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 5.6a2.4 2.4 0 100 4.8 2.4 2.4 0 000-4.8M8 2v1.6M8 12.4V14M2 8h1.6M12.4 8H14M3.8 3.8l1.1 1.1M11.1 11.1l1.1 1.1M12.2 3.8l-1.1 1.1M4.9 11.1l-1.1 1.1" />
    </Svg>
  );
}

/** Funil — filtrar. ⚠️ Nao a lupa: a lupa e PROCURAR, e as duas coisas existem nesta barra. */
export function IconeFunil(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2.5 3.5h11l-4.2 5v4.2l-2.6-1.3V8.5z" />
    </Svg>
  );
}

/** Tabuleiro de entrada — os pedidos que chegam. */
export function IconeCaixaEntrada(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2.5 9.5v3h11v-3M2.5 9.5L4 3.5h8l1.5 6M2.5 9.5h3l.8 1.4h3.4l.8-1.4h3" />
    </Svg>
  );
}

/** Pessoa — a sessão. ⚠️ Substitui o `IconeInfo`, que e a LEGENDA. */
export function IconePessoa(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 3a2.3 2.3 0 100 4.6A2.3 2.3 0 008 3M3.5 13.2c0-2.2 2-3.6 4.5-3.6s4.5 1.4 4.5 3.6" />
    </Svg>
  );
}

export function IconeFechar(p: Props) {
  return (
    <Svg {...p}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </Svg>
  );
}

/**
 * A fila «o que precisa de mim» — um sino.
 *
 * 📌 Um so `path`, como os outros: passar cada icone a um unico `path` recuperou parte do custo
 * medido de trocar emoji por SVG (5,9 -> 5,2 nos/dia, ~29 -> ~24 ms de p95).
 * ⚠️⚠️ **Nao reutilizei o `IconeCaixaEntrada`** porque e o dos «Pedidos»: dois botoes com o mesmo
 * icone na mesma barra ensinam que fazem o mesmo, e esta barra o cliente ja descreveu como
 * confusa.
 */
export function IconeSino(p: Props) {
  return (
    <Svg {...p}>
      <path d="M5.6 11.5h4.8M3.8 11.5a5 5 0 0 0 1.2-3.2V7a3 3 0 0 1 6 0v1.3a5 5 0 0 0 1.2 3.2zM8 2.5v-1" />
    </Svg>
  );
}

/**
 * 2026-11-03 · A caixa de entrada de EMAIL — um envelope. Um só `path`.
 * ⚠️ Não reutiliza `IconeCaixaEntrada` (o dos «Pedidos»): dois botões com o mesmo ícone na barra.
 */
export function IconeEnvelope(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2.5 4h11v8h-11zM2.5 4.5 8 9l5.5-4.5" />
    </Svg>
  );
}

export function IconeVisto(p: Props) {
  return (
    <Svg {...p}>
      <path d="M3 8.5 6.2 12l7-8" />
    </Svg>
  );
}

export function IconeVazio(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2.5 4.5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2z" />
    </Svg>
  );
}

export function IconePaleta(p: Props) {
  return (
    <Svg {...p}>
      <path d="M8 1.8a6.2 6.2 0 1 0 0 12.4c.9 0 1.4-.6 1.4-1.3 0-.8-.6-1.2-.6-1.9 0-.6.5-1.1 1.2-1.1h1.3a2.9 2.9 0 0 0 2.9-2.9C14.2 3.9 11.4 1.8 8 1.8 M5 6h.01 M8 4.5h.01 M5.2 9.4h.01" />
    </Svg>
  );
}

export function IconeInfo(p: Props) {
  return (
    <Svg {...p}>
      <path d="M14.2 8A6.2 6.2 0 1 1 1.8 8a6.2 6.2 0 0 1 12.4 0 M8 7.2v4 M8 4.9h.01" />
    </Svg>
  );
}

/**
 * Voltar atrás — a seta curva do «desfazer».
 *
 * ⚠️ **Um `<path>` só** (P-32): trocar emoji por SVG subiu os nós/dia de 3,7 para 5,9 e o p95 de
 * ~18 para ~29 ms; passar cada ícone a um só caminho recuperou parte disso. Este está na barra de
 * topo, que se desenha uma vez — mas a regra é mecânica, senão o próximo ícone tem dois.
 */
export function IconeDesfazer(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2.6 5.4h4.2 M2.6 5.4V1.9 M2.6 5.4A6 6 0 1 1 2.4 9.6" />
    </Svg>
  );
}

/** O diário — uma página com cabeçalho e duas linhas, para o «Diário» (peça 2 de «D»). */
export function IconeLivro(p: Props) {
  return (
    <Svg {...p}>
      <path d="M2.5 2.5h11v11h-11z M2.5 5.6h11 M4.5 8.3h7 M4.5 10.6h7" />
    </Svg>
  );
}

export function IconePesquisa(p: Props) {
  return (
    <Svg {...p}>
      <path d="M11.5 7a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0 M10.4 10.4 14 14" />
    </Svg>
  );
}
